import { deleteDB, openDB, wrap, type DBSchema, type IDBPDatabase } from 'idb'
import { getSupabaseConfig } from './config'
import { publishAuthIdentity } from '../data/workspace'
import {
  AUTH_DATABASE_NAME,
  AUTH_STORAGE_PREFIX,
  previousBrandStorageName,
} from '../utils/storageNames'

interface AuthStorageSchema extends DBSchema {
  sessions: {
    key: string
    value: {
      key: string
      value: string
    }
  }
  oauthAttempts: {
    key: 'active'
    value: NativeOAuthAttempt
  }
}

export interface NativeOAuthAttempt {
  key: 'active'
  id: string
  redirectUrl: string
  returnPath: string
  createdAt: number
  expiresAt: number
  authorizationCode: string | null
  /** Neon backend only: the app's secret for the app-bound hand-off (apps/auth). Never leaves the device except in the redeem call. */
  verifier?: string | null
}

export interface AsyncAuthStorage {
  getItem(key: string): Promise<string | null>
  setItem(key: string, value: string): Promise<void>
  removeItem(key: string): Promise<void>
}

let authDatabasePromise: Promise<IDBPDatabase<AuthStorageSchema>> | null = null
const PREVIOUS_AUTH_MIGRATION_MARKER = `${AUTH_STORAGE_PREFIX}:migration:previous-brand:v1`
const PREVIOUS_AUTH_OPEN_TIMEOUT_MS = 500

function previousAuthStoragePrefix(): string {
  return `${previousBrandStorageName()}-auth`
}

function openPreviousAuthDatabase(): Promise<IDBPDatabase<AuthStorageSchema> | null> {
  return new Promise((resolve) => {
    let settled = false
    let created = false
    const request = indexedDB.open(previousAuthStoragePrefix())
    const finish = (database: IDBPDatabase<AuthStorageSchema> | null) => {
      if (settled) {
        database?.close()
        return
      }
      settled = true
      globalThis.clearTimeout(timeout)
      resolve(database)
    }
    const timeout = globalThis.setTimeout(() => finish(null), PREVIOUS_AUTH_OPEN_TIMEOUT_MS)
    request.onupgradeneeded = (event) => {
      created = event.oldVersion === 0
    }
    request.onerror = () => finish(null)
    request.onsuccess = () => {
      const database = wrap(request.result) as IDBPDatabase<AuthStorageSchema>
      if (created) {
        database.close()
        finish(null)
        void deleteDB(previousAuthStoragePrefix()).catch(() => undefined)
        return
      }
      finish(database)
    }
  })
}

async function migratePreviousAuthStorage(
  database: IDBPDatabase<AuthStorageSchema>,
): Promise<void> {
  if (await database.get('sessions', PREVIOUS_AUTH_MIGRATION_MARKER)) return

  const currentKeys = await database.getAllKeys('sessions')
  if (currentKeys.some((key) => key !== PREVIOUS_AUTH_MIGRATION_MARKER)) {
    await database.put('sessions', {
      key: PREVIOUS_AUTH_MIGRATION_MARKER,
      value: 'complete',
    })
    void deleteDB(previousAuthStoragePrefix()).catch(() => undefined)
    return
  }

  const availableDatabases = typeof indexedDB.databases === 'function'
    ? await indexedDB.databases().catch(() => null)
    : null
  if (availableDatabases && !availableDatabases.some(({ name }) => name === previousAuthStoragePrefix())) {
    await database.put('sessions', {
      key: PREVIOUS_AUTH_MIGRATION_MARKER,
      value: 'complete',
    })
    return
  }

  const previousDatabase = await openPreviousAuthDatabase()
  if (!previousDatabase) return
  try {
    if (previousDatabase.objectStoreNames.contains('sessions')) {
      const entries = await previousDatabase.getAll('sessions')
      for (const entry of entries) {
        const previousPrefix = previousAuthStoragePrefix()
        const key = entry.key.startsWith(previousPrefix)
          ? `${AUTH_STORAGE_PREFIX}${entry.key.slice(previousPrefix.length)}`
          : entry.key
        if (await database.get('sessions', key)) continue
        await database.put('sessions', { key, value: entry.value })
      }
    }
    await database.put('sessions', {
      key: PREVIOUS_AUTH_MIGRATION_MARKER,
      value: 'complete',
    })
  } finally {
    previousDatabase.close()
  }
  void deleteDB(previousAuthStoragePrefix()).catch(() => undefined)
}

function getAuthDatabase(): Promise<IDBPDatabase<AuthStorageSchema>> {
  authDatabasePromise ??= openDB<AuthStorageSchema>(AUTH_DATABASE_NAME, 2, {
    upgrade(database, oldVersion) {
      if (oldVersion < 1) database.createObjectStore('sessions', { keyPath: 'key' })
      if (oldVersion < 2) database.createObjectStore('oauthAttempts', { keyPath: 'key' })
    },
  }).then(async (database) => {
    await migratePreviousAuthStorage(database)
    return database
  })
  return authDatabasePromise
}

export const indexedDbAuthStorage: AsyncAuthStorage = {
  async getItem(key) {
    const entry = await (await getAuthDatabase()).get('sessions', key)
    return entry?.value ?? null
  },
  async setItem(key, value) {
    const database = await getAuthDatabase()
    const config = getSupabaseConfig()
    if (config && key === `${AUTH_STORAGE_PREFIX}-${config.environment}`) {
      const session = JSON.parse(value)
      publishAuthIdentity({ project: config.url, environment: config.environment }, typeof session?.user?.id === 'string' ? session.user.id : null)
    }
    await database.put('sessions', { key, value })
  },
  async removeItem(key) {
    const database = await getAuthDatabase()
    const config = getSupabaseConfig()
    if (config && key === `${AUTH_STORAGE_PREFIX}-${config.environment}`) publishAuthIdentity({ project: config.url, environment: config.environment }, null)
    await database.delete('sessions', key)
  },
}

export const nativeOAuthAttemptStorage = {
  async read(): Promise<NativeOAuthAttempt | null> {
    return (await (await getAuthDatabase()).get('oauthAttempts', 'active')) ?? null
  },
  async write(attempt: NativeOAuthAttempt): Promise<void> {
    await (await getAuthDatabase()).put('oauthAttempts', attempt)
  },
  async clear(expectedId?: string): Promise<boolean> {
    const database = await getAuthDatabase()
    const transaction = database.transaction('oauthAttempts', 'readwrite')
    const current = await transaction.store.get('active')
    const shouldClear = Boolean(current && (!expectedId || current.id === expectedId))
    if (shouldClear) await transaction.store.delete('active')
    await transaction.done
    return shouldClear
  },
}

async function closeAuthStorageForTests(): Promise<void> {
  if (!authDatabasePromise) return
  const database = await authDatabasePromise
  database.close()
  authDatabasePromise = null
}

export async function resetAuthStorageForTests(): Promise<void> {
  await closeAuthStorageForTests()
  await deleteDB(AUTH_DATABASE_NAME)
  await deleteDB(previousAuthStoragePrefix())
}
